import { Link } from "react-router-dom"
import type { NostrEvent } from "@/nostr/types"
import { extractMedia, parseContent, type ContentSegment } from "@/utils/content"
import { noteIdOf, npubOf } from "@/utils/nip19"
import { simplifyUrl } from "@/utils/url"

function MediaImage({ url, alt }: { url: string; alt?: string }) {
  return (
    <a className="note-media" href={url} target="_blank" rel="noopener noreferrer nofollow">
      <img src={url} alt={alt ?? ""} loading="lazy" decoding="async" />
    </a>
  )
}

function Segment({
  segment,
  onHashtagClick,
}: {
  segment: ContentSegment
  onHashtagClick?: (tag: string) => void
}) {
  switch (segment.type) {
    case "text":
      return <>{segment.text}</>
    case "link":
      return (
        <a href={segment.url} target="_blank" rel="noopener noreferrer nofollow" className="note-link">
          {simplifyUrl(segment.url)}
        </a>
      )
    case "image":
      return <MediaImage url={segment.url} alt={segment.alt} />
    case "video":
      return (
        <video className="note-video" src={segment.url} controls preload="metadata" playsInline />
      )
    case "audio":
      return <audio className="note-audio" src={segment.url} controls preload="metadata" />
    case "hashtag":
      return (
        <button
          type="button"
          className="hashtag"
          onClick={() => onHashtagClick?.(segment.tag)}
        >
          #{segment.tag}
        </button>
      )
    case "nostr": {
      const { entity } = segment
      if (entity.type === "npub" || entity.type === "nprofile") {
        return (
          <Link to={`/u/${npubOf(entity.pubkey)}`} className="mention">
            @{npubOf(entity.pubkey).slice(0, 10)}…
          </Link>
        )
      }
      if (entity.type === "note" || entity.type === "nevent") {
        return (
          <Link to={`/n/${noteIdOf(entity.id)}`} className="mention">
            note {entity.id.slice(0, 8)}…
          </Link>
        )
      }
      return <span className="mention">addressable event</span>
    }
    default:
      return null
  }
}

export interface NoteContentProps {
  event: NostrEvent
  className?: string
  onHashtagClick?: (tag: string) => void
}

/**
 * Renders note content: text, links, inline media, NIP-21 entities, hashtags
 * and NIP-92 media tags. Newlines are preserved through CSS (`pre-wrap`).
 */
export function NoteContent({ event, className = "", onHashtagClick }: NoteContentProps) {
  const segments = parseContent(event.content)
  const media = extractMedia(event.content, event.tags)
  const inlineMedia = new Set(
    segments.flatMap((segment) =>
      segment.type === "image" || segment.type === "video" || segment.type === "audio"
        ? [segment.url]
        : [],
    ),
  )
  const extraMedia = media.filter((item) => !inlineMedia.has(item.url))

  return (
    <div className={`note-content ${className}`}>
      <p className="note-text">
        {segments.map((segment, index) => (
          <Segment key={`${segment.type}-${index}`} segment={segment} onHashtagClick={onHashtagClick} />
        ))}
      </p>
      {extraMedia.length > 0 && (
        <div className="note-media-grid">
          {extraMedia.map((item) =>
            item.type === "image" ? (
              <MediaImage key={item.url} url={item.url} alt={item.alt} />
            ) : item.type === "video" ? (
              <video key={item.url} className="note-video" src={item.url} controls preload="metadata" playsInline />
            ) : (
              <audio key={item.url} className="note-audio" src={item.url} controls preload="metadata" />
            ),
          )}
        </div>
      )}
    </div>
  )
}
